import type {
  BatchArgs,
  BatchResponse,
  EndpointOptions,
  IOrganizationRole,
  Role,
} from '@carboncopyco/types';
import { Debug, removeUndefinedValues } from '@carboncopyco/utils';
import { handleBatchRequest } from '../utils/handleBatchRequest';
import { mainApi } from './mainApi';

Debug.extend('demostack-data:api:roleApi');

export interface CreateRoleArgs {
  email: string;
  organizationId: string;
  role: Role;
}

export interface DeleteRoleArgs {
  organizationId: string;
  id: string;
}

export interface GetRoleArgs {
  organizationId: string;
  id: string;
}

export interface UpdateRoleArgs {
  organizationId: string;
  id: string;
  role: Role;
  options?: EndpointOptions;
}

export interface GetRolesArgs extends BatchArgs {
  organizationId: string;
  query?: string;
}

type GetRolesCSVResponse = Record<'signedUrl', string>;
type CreateRoleResponse = IOrganizationRole;
type DeleteRoleResponse = IOrganizationRole;
type GetRoleResponse = IOrganizationRole;
type UpdateRoleResponse = IOrganizationRole;

const roleApi = mainApi.injectEndpoints({
  endpoints: (builder) => ({
    createRole: builder.mutation<CreateRoleResponse, CreateRoleArgs>({
      query: ({ email, organizationId, role }) => ({
        url: '/api/roles/new',
        method: 'PUT',
        body: { email, organizationId, role },
      }),
      invalidatesTags: ['Roles'],
    }),

    deleteRole: builder.mutation<DeleteRoleResponse, DeleteRoleArgs>({
      query: ({ organizationId, id }) => ({
        url: `/api/${organizationId}/role/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, { id }) => [
        { type: 'Role', id },
        { type: 'Roles' },
      ],
    }),

    getRole: builder.query<GetRoleResponse, GetRoleArgs>({
      query: ({ organizationId, id }) => ({
        url: `/api/${organizationId}/role/${id}`,
        method: 'GET',
      }),
      providesTags: (_, __, { id }) => [{ type: 'Role', id }],
    }),

    updateRole: builder.mutation<UpdateRoleResponse, UpdateRoleArgs>({
      query: ({ organizationId, id, role }) => ({
        url: `/api/${organizationId}/role/${id}`,
        method: 'POST',
        body: { role },
      }),
      invalidatesTags: (_, __, { id, options }) =>
        options?.preventInvalidate
          ? []
          : [{ type: 'Roles' }, { type: 'Role', id }],
    }),

    getRoles: builder.query<BatchResponse<IOrganizationRole>, GetRolesArgs>({
      query: ({ organizationId, query, ...args }) =>
        handleBatchRequest({
          ...args,
          limit: 100,
          params: removeUndefinedValues({ query }),
          url: `/api/${organizationId}/roles`,
        }),

      providesTags: ['Roles'],
    }),

    getRolesCsv: builder.query<GetRolesCSVResponse, { organizationId: string }>(
      {
        query: ({ organizationId }) => ({
          url: `/api/${organizationId}/roles/csv`,
        }),
      }
    ),
  }),
});

export const {
  useCreateRoleMutation,
  useDeleteRoleMutation,
  useGetRoleQuery,
  useUpdateRoleMutation,
  useGetRolesQuery,
  useLazyGetRolesQuery,
  useGetRolesCsvQuery,
  useLazyGetRolesCsvQuery,
} = roleApi;
